import { Settings, TriangleAlert } from 'lucide-react'
import { Suspense } from 'react'
import { useMatch, useParams } from 'react-router'
import { LayoutPage } from '@/components/layout/page'
import { H3 } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useCompanyPath } from '@/lib/hooks'
import { OverviewTabFallback } from '@/routes/webhook/tabs/overview/fallback'
import { RollWebhookSecretDialog } from '@/routes/webhook/tabs/overview/roll-webhook-secret-dialog'
import { DeleteWebhook } from '@/routes/webhook/tabs/overview/sections/danger-zone/delete-webhook'
import { DeleteWebhookFallback } from '@/routes/webhook/tabs/overview/sections/danger-zone/delete-webhook/fallback'
import { ToggleWebhook } from '@/routes/webhook/tabs/overview/sections/danger-zone/toggle-webhook'
import { ToggleWebhookFallback } from '@/routes/webhook/tabs/overview/sections/danger-zone/toggle-webhook/fallback'
import { WebhookAnalyticsSection } from '@/routes/webhook/tabs/overview/sections/general/analytics'
import { WebhookAnalyticsSectionFallback } from '@/routes/webhook/tabs/overview/sections/general/analytics/fallback'
import { WebhookDetailsSection } from '@/routes/webhook/tabs/overview/sections/general/details'
import { WebhookDetailsSectionFallback } from '@/routes/webhook/tabs/overview/sections/general/details/fallback'
import { UpdateWebhookSheet } from '@/routes/webhook/tabs/overview/update-webhook-sheet'

export const OverviewTab = () => {
  const { idWebhook = '' } = useParams()
  const { navigateToPath } = useCompanyPath()

  const isUpdateWebhookSheetOpen = Boolean(
    useMatch('/webhook/:idWebhook/overview/update')
  )
  const isRollSecretDialogOpen = Boolean(
    useMatch('/webhook/:idWebhook/overview/roll-secret')
  )

  return (
    <LayoutPage
      browserTitle="Webhook overview"
      fallback={<OverviewTabFallback />}
    >
      <div className="space-y-6 pb-25">
        <Card className="bg-secondary p-0 gap-0">
          <CardHeader className="px-5 pt-3 pb-1">
            <CardTitle>
              <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
                <Settings className="size-4" /> General
              </H3>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Card className="p-0 border-x-0 border-b-0 shadow-none">
              <CardContent className="p-0 pb-6">
                <Suspense fallback={<WebhookDetailsSectionFallback />}>
                  <WebhookDetailsSection />
                </Suspense>
                <Separator className="w-full mt-6" />
                <Suspense fallback={<WebhookAnalyticsSectionFallback />}>
                  <WebhookAnalyticsSection />
                </Suspense>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
        <Card className="bg-secondary p-0 gap-0">
          <CardHeader className="px-5 pt-3 pb-1">
            <CardTitle>
              <H3 className="flex items-center gap-2 text-sm text-destructive/80">
                <TriangleAlert className="size-4" /> Danger zone
              </H3>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Card className="p-0 border-x-0 border-b-0 shadow-none">
              <CardContent className="p-0 pb-6">
                <Suspense fallback={<ToggleWebhookFallback />}>
                  <ToggleWebhook />
                </Suspense>
                <Separator className="w-full mt-6" />
                <Suspense fallback={<DeleteWebhookFallback />}>
                  <DeleteWebhook />
                </Suspense>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
      <UpdateWebhookSheet
        isOpen={Boolean(isUpdateWebhookSheetOpen)}
        onClose={() =>
          navigateToPath({
            pathname: '/webhook/:idWebhook/overview',
            variables: { idWebhook },
            options: { replace: true },
          })
        }
      />
      <RollWebhookSecretDialog
        isOpen={Boolean(isRollSecretDialogOpen)}
        onClose={() =>
          navigateToPath({
            pathname: '/webhook/:idWebhook/overview',
            variables: { idWebhook },
            options: { replace: true },
          })
        }
      />
    </LayoutPage>
  )
}
